/**
 * Textos de la interfaz en español, inglés y chino.
 *
 * Las traducciones viven aquí como archivos estáticos, no se piden a ninguna
 * API en tiempo de ejecución: desde China los servicios de traducción de
 * Google están bloqueados, y una pantalla que depende de eso queda a medias
 * justo donde más se necesita.
 *
 * GLOSARIO DEL SII
 * ----------------
 * Los términos tributarios chilenos están traducidos a mano y revisados. Nunca
 * los reemplaces por traducción automática: "folio" no es "página", es el
 * número correlativo que el SII autoriza para un tipo de documento, y
 * traducirlo mal hace incomprensible toda la pantalla.
 *
 * Los nombres propios NO se traducen: SII, DTE, CAF, RUT. Son identificadores
 * del sistema tributario chileno y se usan igual en cualquier idioma.
 */

export const IDIOMAS = [
  { codigo: "es", nombre: "Español", bandera: "ES" },
  { codigo: "en", nombre: "English", bandera: "EN" },
  { codigo: "zh", nombre: "中文", bandera: "ZH" },
];

export const IDIOMA_POR_DEFECTO = "es";

const textos = {
  // --- menú y navegación ---------------------------------------------------
  "menu.dashboard": { es: "Panel principal", en: "Dashboard", zh: "主面板" },
  "menu.emitidos": { es: "DTE emitidos", en: "Issued documents", zh: "已开具单据" },
  "menu.compras": { es: "DTE compras", en: "Purchase documents", zh: "采购单据" },
  "menu.libros": { es: "Libros DTE", en: "Tax books", zh: "税务账簿" },
  "menu.caf": { es: "Folios CAF", en: "CAF folios", zh: "CAF 号段" },
  "menu.perfil": { es: "Perfil", en: "Profile", zh: "个人资料" },
  "menu.cerrarSesion": { es: "Cerrar sesión", en: "Sign out", zh: "退出登录" },

  // --- glosario del SII: revisado a mano, no tocar sin criterio ------------
  "dte.factura": { es: "Factura", en: "Invoice", zh: "增值税发票" },
  "dte.facturaExenta": { es: "Factura exenta", en: "Tax-exempt invoice", zh: "免税发票" },
  "dte.boleta": { es: "Boleta", en: "Receipt", zh: "收据" },
  "dte.boletaExenta": { es: "Boleta exenta", en: "Tax-exempt receipt", zh: "免税收据" },
  "dte.guia": { es: "Guía de despacho", en: "Dispatch note", zh: "送货单" },
  "dte.notaCredito": { es: "Nota de crédito", en: "Credit note", zh: "红字发票" },
  "dte.notaDebito": { es: "Nota de débito", en: "Debit note", zh: "蓝字补充发票" },

  "sii.folio": { es: "Folio", en: "Folio number", zh: "单据号" },
  "sii.caf": { es: "CAF", en: "CAF (folio authorization)", zh: "CAF（号段授权）" },
  "sii.timbre": { es: "Timbre electrónico", en: "Electronic stamp", zh: "电子印章" },
  "sii.trackId": { es: "Identificador de envío", en: "Submission ID", zh: "提交编号" },

  // Ambiente del SII. Un rol de solo lectura no puede CAMBIARLO —el selector
  // le aparece deshabilitado y fijo en producción por `restrict_modo`— pero sí
  // lo VE, así que necesita estar traducido.
  "sii.certificacion": { es: "Certificación", en: "Certification", zh: "测试环境" },
  "sii.produccion": { es: "Producción", en: "Production", zh: "正式环境" },
  "filtro.modo": { es: "Modo", en: "Environment", zh: "环境" },
  "filtro.todosModos": { es: "Todos", en: "All", zh: "全部" },

  // --- estados de un documento ---------------------------------------------
  "estado.aceptado": { es: "Aceptado", en: "Accepted", zh: "已接受" },
  "estado.rechazado": { es: "Rechazado", en: "Rejected", zh: "已拒绝" },
  "estado.reparos": { es: "Aceptado con reparos", en: "Accepted with remarks", zh: "有保留接受" },
  "estado.enviado": { es: "Enviado", en: "Submitted", zh: "已提交" },
  "estado.pendiente": { es: "Pendiente", en: "Pending", zh: "待处理" },
  "estado.error": { es: "Error", en: "Error", zh: "错误" },

  // --- columnas de las tablas ----------------------------------------------
  "col.tipo": { es: "Tipo", en: "Type", zh: "类型" },
  "col.folio": { es: "Folio", en: "Folio", zh: "单据号" },
  // Copias del mismo documento que el proveedor reenvió por correo.
  "compras.copias": {
    es: "+{n} copias",
    en: "+{n} copies",
    zh: "+{n} 份重复",
  },
  "compras.copiasAyuda": {
    es: "El proveedor reenvió este documento {n} vez/veces más. Se muestra una sola: el mismo folio del mismo emisor es el mismo documento, y contarlo de nuevo inflaría el total comprado.",
    en: "The supplier re-sent this document {n} more time(s). Only one is shown: the same folio from the same issuer is the same document, and counting it again would inflate the purchase total.",
    zh: "供应商重复发送了该单据 {n} 次。仅显示一条：同一开票方的同一单据号即同一单据，重复计算会虚增采购总额。",
  },
  "col.fecha": { es: "Fecha", en: "Date", zh: "日期" },
  "col.fechaEmision": { es: "Fecha de emisión", en: "Issue date", zh: "开具日期" },
  "col.receptor": { es: "Receptor", en: "Recipient", zh: "接收方" },
  "col.emisor": { es: "Emisor", en: "Issuer", zh: "开具方" },
  "col.rut": { es: "RUT", en: "Tax ID (RUT)", zh: "纳税人识别号" },
  "col.razonSocial": { es: "Razón social", en: "Company name", zh: "公司名称" },
  "col.neto": { es: "Neto", en: "Net amount", zh: "不含税金额" },
  "col.iva": { es: "IVA", en: "VAT", zh: "增值税" },
  "col.exento": { es: "Exento", en: "Tax-exempt", zh: "免税金额" },
  "col.total": { es: "Total", en: "Total", zh: "总计" },
  "col.estado": { es: "Estado", en: "Status", zh: "状态" },
  "col.acciones": { es: "Acciones", en: "Actions", zh: "操作" },
  "col.disponibles": { es: "Disponibles", en: "Available", zh: "可用" },
  "col.rango": { es: "Rango", en: "Range", zh: "范围" },
  "col.proveedor": { es: "Proveedor", en: "Supplier", zh: "供应商" },
  "col.periodo": { es: "Período", en: "Period", zh: "期间" },
  "col.adjuntos": { es: "Adjuntos", en: "Attachments", zh: "附件" },
  "col.totalCompra": { es: "Total compra", en: "Purchase total", zh: "采购总额" },
  "col.recibido": { es: "Recibido", en: "Received", zh: "接收时间" },
  "col.codEnvio": { es: "Cód. envío", en: "Submission code", zh: "提交代码" },

  // --- acciones -------------------------------------------------------------
  "accion.buscar": { es: "Buscar", en: "Search", zh: "搜索" },
  "busqueda.folio": { es: "N° de documento", en: "Document number", zh: "单据号" },
  "busqueda.folioAyuda": { es: "Ej: 4676", en: "e.g. 4676", zh: "例如：4676" },
  "busqueda.proveedor": { es: "Proveedor", en: "Supplier", zh: "供应商" },
  "busqueda.proveedorAyuda": { es: "Parte del nombre", en: "Part of the name", zh: "名称的一部分" },
  "accion.filtrar": { es: "Filtrar", en: "Filter", zh: "筛选" },
  "accion.limpiar": { es: "Limpiar", en: "Clear", zh: "清除" },
  "accion.exportar": { es: "Exportar", en: "Export", zh: "导出" },
  "accion.exportarExcel": { es: "Exportar a Excel", en: "Export to Excel", zh: "导出到 Excel" },
  "accion.descargarPdf": { es: "Descargar PDF", en: "Download PDF", zh: "下载 PDF" },
  "accion.descargarXml": { es: "Descargar XML", en: "Download XML", zh: "下载 XML" },
  "accion.ver": { es: "Ver", en: "View", zh: "查看" },
  "accion.actualizar": { es: "Actualizar", en: "Refresh", zh: "刷新" },
  "accion.cerrar": { es: "Cerrar", en: "Close", zh: "关闭" },

  // --- mensajes de estado ---------------------------------------------------
  "msg.cargando": { es: "Cargando…", en: "Loading…", zh: "加载中…" },
  "msg.sinResultados": { es: "Sin resultados", en: "No results", zh: "无结果" },
  "msg.sinDocumentos": { es: "No hay documentos", en: "No documents", zh: "暂无单据" },
  "msg.error": { es: "Ocurrió un error", en: "Something went wrong", zh: "发生错误" },
  "msg.desde": { es: "Desde", en: "From", zh: "从" },
  "msg.hasta": { es: "Hasta", en: "To", zh: "至" },
  "msg.pagina": { es: "Página", en: "Page", zh: "页" },
  "msg.de": { es: "de", en: "of", zh: "共" },
  "msg.registros": { es: "registros", en: "records", zh: "条记录" },


  // --- filtros y búsqueda ---------------------------------------------------
  "filtro.estadoProceso": { es: "Estado proceso", en: "Processing status", zh: "处理状态" },
  "filtro.estadoCiclo": { es: "Estado ciclo", en: "Lifecycle status", zh: "生命周期状态" },
  "filtro.emailProveedor": { es: "Email proveedor", en: "Supplier email", zh: "供应商邮箱" },
  "filtro.emailAyuda": { es: "Opcional, coincide exacto", en: "Optional, exact match", zh: "可选，需完全匹配" },
  "filtro.filasPorPagina": { es: "Filas por página", en: "Rows per page", zh: "每页行数" },
  "filtro.limite": { es: "Límite", en: "Limit", zh: "上限" },
  "filtro.recibidoDesde": { es: "Recibido desde", en: "Received from", zh: "接收起始日" },
  "filtro.recibidoHasta": { es: "Recibido hasta", en: "Received until", zh: "接收截止日" },
  "filtro.emisionDesde": { es: "Fecha emisión desde", en: "Issued from", zh: "开具起始日" },
  "filtro.emisionHasta": { es: "Fecha emisión hasta", en: "Issued until", zh: "开具截止日" },
  "filtro.mesActual": { es: "Mes actual", en: "Current month", zh: "本月" },
  "filtro.limpiar": { es: "Limpiar filtros", en: "Clear filters", zh: "清除筛选" },
  "filtro.limpiarTodo": { es: "Limpiar filtros y búsqueda", en: "Clear filters and search", zh: "清除筛选和搜索" },
  "filtro.enPagina": { es: "En página", en: "On this page", zh: "本页" },

  // opciones de estado (compras)
  "opcion.todosEstados": { es: "Todos los estados", en: "All statuses", zh: "所有状态" },
  "opcion.recibido": { es: "Recibido", en: "Received", zh: "已接收" },
  "opcion.parseado": { es: "Parseado", en: "Parsed", zh: "已解析" },
  "opcion.validado": { es: "Validado", en: "Validated", zh: "已验证" },
  "opcion.aceptado": { es: "Aceptado", en: "Accepted", zh: "已接受" },
  "opcion.rechazado": { es: "Rechazado", en: "Rejected", zh: "已拒绝" },
  "opcion.error": { es: "Error", en: "Error", zh: "错误" },

  // acciones de la pantalla de compras
  "accion.abrirEnlace": { es: "Abrir enlace", en: "Open link", zh: "打开链接" },
  "accion.aceptarManual": { es: "Aceptar manual", en: "Accept manually", zh: "手动接受" },
  "accion.cancelar": { es: "Cancelar", en: "Cancel", zh: "取消" },


  // --- tipos de DTE que faltaban -------------------------------------------
  "dte.liquidacion": { es: "Liquidación", en: "Settlement invoice", zh: "结算发票" },
  "dte.facturaCompra": { es: "Factura de compra", en: "Self-billed invoice", zh: "自开采购发票" },
  "dte.pagoElectronico": { es: "Pago electrónico", en: "Electronic payment", zh: "电子支付" },
  "dte.facturaExportacion": { es: "Factura exportación", en: "Export invoice", zh: "出口发票" },
  "dte.notaDebitoExportacion": { es: "N. débito exportación", en: "Export debit note", zh: "出口蓝字补充发票" },
  "dte.notaCreditoExportacion": { es: "N. crédito exportación", en: "Export credit note", zh: "出口红字发票" },

  // --- cabeceras de pantalla ------------------------------------------------
  "compras.titulo": { es: "DTE Compras (correo)", en: "Purchase documents (email)", zh: "采购单据（邮件）" },
  "compras.descripcion": {
    es: "Listado de compras recibidas por la casilla Gmail conectada al sistema. Cada fila corresponde a un correo procesado.",
    en: "Purchase documents received in the Gmail inbox connected to the system. Each row is one processed email.",
    zh: "系统所连接的 Gmail 邮箱收到的采购单据。每一行对应一封已处理的邮件。",
  },
  "compras.ultimaSync": { es: "Última sincronización", en: "Last sync", zh: "最近同步" },
  "compras.sinEjecutar": { es: "sin ejecutar", en: "never run", zh: "尚未运行" },
  "emitidos.titulo": { es: "DTE emitidos", en: "Issued documents", zh: "已开具单据" },

  // --- indicadores de la parte superior ------------------------------------
  "kpi.conXml": { es: "Con XML", en: "With XML", zh: "含 XML" },
  "kpi.conPdf": { es: "Con PDF", en: "With PDF", zh: "含 PDF" },
  "kpi.aceptadas": { es: "Aceptadas", en: "Accepted", zh: "已接受" },
  "kpi.totalComprado": { es: "Total comprado", en: "Total purchased", zh: "采购总额" },
  "kpi.compras": { es: "Compras", en: "Purchases", zh: "采购笔数" },
  "kpi.porResponder": { es: "Por responder", en: "Awaiting response", zh: "待回复" },
  "kpi.criticas": { es: "Vence el plazo", en: "Deadline closing", zh: "即将到期" },
  "kpi.criticasPie": { es: "Responde hoy o el SII las acepta solo", en: "Respond today or the SII accepts them automatically", zh: "请今日回复，否则 SII 将自动接受" },
  "kpi.sinMonto": { es: "Sin monto", en: "Amount missing", zh: "缺少金额" },
  "kpi.sinMontoPie": { es: "El total de arriba está incompleto", en: "The total above is incomplete", zh: "上方总额不完整" },
  "kpi.netoConNotas": { es: "Neto, con notas de crédito descontadas", en: "Net, credit notes deducted", zh: "净额，已扣除红字发票" },
  "vista.compras": { es: "Compras", en: "Purchases", zh: "采购" },
  "vista.acuses": { es: "Acuses de clientes", en: "Customer acknowledgements", zh: "客户回执" },
  "vista.acusesAyuda": { es: "Respuestas que mandan tus clientes al recibir una factura tuya. No son compras: no tienen proveedor ni monto, y no hay que responderlas.", en: "Replies your customers send when they receive one of your invoices. These are not purchases: they have no supplier or amount, and need no response.", zh: "客户收到你开具的发票后自动回复的回执。这些不是采购：没有供应商和金额，也无需回复。" },
  "grupo.titulo": { es: "Tipo de documento", en: "Document type", zh: "单据类型" },
  "grupo.todos": { es: "Todos", en: "All", zh: "全部" },
  "grupo.facturas": { es: "Facturas", en: "Invoices", zh: "发票" },
  "grupo.guias": { es: "Guías de despacho", en: "Dispatch notes", zh: "送货单" },
  "grupo.notasCredito": { es: "Notas de crédito", en: "Credit notes", zh: "红字发票" },
  "grupo.notasDebito": { es: "Notas de débito", en: "Debit notes", zh: "蓝字发票" },
  "kpi.errores": { es: "Errores", en: "Errors", zh: "错误" },

  // --- botones que faltaban -------------------------------------------------
  "accion.actualizando": { es: "Actualizando…", en: "Refreshing…", zh: "刷新中…" },
  "accion.resincronizar": { es: "Re-sincronizar Gmail", en: "Re-sync Gmail", zh: "重新同步 Gmail" },
  "accion.sincronizando": { es: "Sincronizando Gmail…", en: "Syncing Gmail…", zh: "正在同步 Gmail…" },
  "accion.exportarDetalle": { es: "Exportar Excel (detalle)", en: "Export Excel (line detail)", zh: "导出 Excel（明细）" },
  "accion.exportarDetalleAyuda": {
    es: "Excel con una fila por producto de cada compra",
    en: "Excel with one row per line item",
    zh: "Excel，每个商品一行",
  },
  "accion.exportando": { es: "Exportando…", en: "Exporting…", zh: "导出中…" },
  "accion.generando": { es: "Generando", en: "Generating", zh: "生成中" },
  "accion.aceptando": { es: "Aceptando", en: "Accepting", zh: "接受中" },
  "accion.aceptacionManual": { es: "Aceptación manual", en: "Manual acceptance", zh: "手动接受" },
  "filtro.titulo": { es: "Filtros", en: "Filters", zh: "筛选条件" },

  // --- tiempo relativo ------------------------------------------------------
  // Se arma por partes para que cada idioma ordene la frase a su manera:
  // "hace 2 h" / "2 h ago" / "2 小时前".
  "tiempo.instantes": { es: "hace instantes", en: "just now", zh: "刚刚" },
  "tiempo.min": { es: "min", en: "min", zh: "分钟" },
  "tiempo.hora": { es: "h", en: "h", zh: "小时" },
  "tiempo.dia": { es: "día", en: "day", zh: "天" },
  "tiempo.dias": { es: "días", en: "days", zh: "天" },
  "tiempo.mes": { es: "mes", en: "month", zh: "个月" },
  "tiempo.meses": { es: "meses", en: "months", zh: "个月" },
  "tiempo.anio": { es: "año", en: "year", zh: "年" },
  "tiempo.anios": { es: "años", en: "years", zh: "年" },


  // --- estados del ciclo de vida (documentos emitidos) ---------------------
  "estado.generado": { es: "Generado", en: "Rendered", zh: "已生成" },
  "estado.anulado": { es: "Anulado", en: "Cancelled", zh: "已作废" },

  // --- pantalla de documentos emitidos --------------------------------------
  "emitidos.tituloTarjeta": { es: "Documentos DTE", en: "DTE documents", zh: "DTE 单据" },
  "emitidos.descripcion": {
    es: "Listado de documentos emitidos. Ordena por columna con las flechas del encabezado.",
    en: "Documents issued by this company. Sort by column using the arrows in the header.",
    zh: "本公司已开具的单据。点击表头箭头可按列排序。",
  },
  "filtro.tipoDte": { es: "Tipo DTE", en: "Document type", zh: "单据类型" },
  "filtro.cualquiera": { es: "Cualquiera", en: "Any", zh: "任意" },
  "filtro.rutEmisor": { es: "RUT emisor", en: "Issuer tax ID", zh: "开具方纳税人识别号" },
  "accion.verOcultos": { es: "Ver ocultos", en: "Show hidden", zh: "查看已隐藏" },
  "accion.verActivos": { es: "Ver activos", en: "Show active", zh: "查看有效单据" },


  "accion.generarPdf": { es: "Generar PDF", en: "Generate PDF", zh: "生成 PDF" },
  "msg.sinRegistros": {
    es: "No hay registros. Revisa la integración con Gmail o los filtros.",
    en: "No records. Check the Gmail integration or the filters.",
    zh: "暂无记录。请检查 Gmail 集成或筛选条件。",
  },


  // --- respuesta comercial a una factura de compra --------------------------
  // Estos textos describen decisiones con efecto legal. El chino está en
  // términos contables reales, no traducción literal: 红字发票 (nota de
  // crédito), 有异议接受 (aceptar con reservas/discrepancias).
  "respuesta.titulo": { es: "Responder al proveedor", en: "Respond to supplier", zh: "答复供应商" },
  "respuesta.aceptar": { es: "Aceptar", en: "Accept", zh: "接受" },
  "respuesta.aceptarDetalle": {
    es: "La factura está correcta. Se acepta y se confirma la recepción de la mercadería.",
    en: "The invoice is correct. It is accepted and receipt of the goods is confirmed.",
    zh: "发票无误。接受该发票并确认已收到货物。",
  },
  "respuesta.discrepancias": { es: "Aceptar con discrepancias", en: "Accept with discrepancies", zh: "有异议接受" },
  "respuesta.discrepanciasDetalle": {
    es: "Se acepta la factura, pero queda registrada una diferencia (precio, cantidad u otra).",
    en: "The invoice is accepted, but a difference is recorded (price, quantity or other).",
    zh: "接受发票，但同时记录差异（价格、数量或其他）。",
  },
  "respuesta.rechazar": { es: "Rechazar", en: "Reject", zh: "拒绝" },
  "respuesta.rechazarDetalle": {
    es: "La factura no corresponde. No se emite acuse de recibo de mercadería.",
    en: "The invoice is not valid. No goods-receipt acknowledgement is issued.",
    zh: "该发票不成立。不出具收货确认书。",
  },
  "respuesta.motivoObligatorio": { es: "Motivo (obligatorio)", en: "Reason (required)", zh: "原因（必填）" },
  "respuesta.motivoOpcional": { es: "Motivo (opcional)", en: "Reason (optional)", zh: "原因（选填）" },
  "respuesta.motivoAyuda": {
    es: "Ej: la mercadería nunca llegó",
    en: "e.g. the goods never arrived",
    zh: "例如：货物从未送达",
  },
  "respuesta.motivoFalta": {
    es: "Esta decisión necesita un motivo: el proveedor lo va a leer.",
    en: "This decision needs a reason: the supplier will read it.",
    zh: "此决定需要填写原因：供应商会看到。",
  },
  "respuesta.advertencia": {
    es: "Se enviará un documento firmado al proveedor y no se puede retirar. El SII acepta la factura automáticamente a los 8 días.",
    en: "A signed document will be sent to the supplier and cannot be withdrawn. The SII accepts the invoice automatically after 8 days.",
    zh: "系统将向供应商发送一份已签名的文件，且无法撤回。超过 8 天后，SII 将自动视为接受该发票。",
  },
  "respuesta.confirmar": { es: "Enviar respuesta", en: "Send response", zh: "发送答复" },
  "respuesta.enviando": { es: "Enviando…", en: "Sending…", zh: "发送中…" },
  "respuesta.responder": { es: "Responder", en: "Respond", zh: "答复" },
  "respuesta.enviada": { es: "Respuesta enviada al proveedor.", en: "Response sent to the supplier.", zh: "答复已发送给供应商。" },
  // --- reproceso de una compra que quedó en error --------------------------
  "accion.reprocesar": { es: "Reprocesar", en: "Reprocess", zh: "重新处理" },
  "accion.reprocesando": { es: "Reprocesando", en: "Reprocessing", zh: "重新处理中" },
  "reproceso.confirmar": {
    es: "Se va a procesar esta compra otra vez, desde el correo original del proveedor. No consume folios y al proveedor no le llega nada. ¿Continuar?",
    en: "This purchase will be processed again from the supplier's original email. It does not consume folios and nothing is sent to the supplier. Continue?",
    zh: "系统将根据供应商的原始邮件重新处理这笔采购。不会消耗单据号，也不会向供应商发送任何内容。是否继续？",
  },
  "reproceso.ok": {
    es: "Compra reprocesada correctamente.",
    en: "Purchase reprocessed successfully.",
    zh: "采购已成功重新处理。",
  },
  "reproceso.sigueEnError": {
    es: "La compra volvió a quedar en error:",
    en: "The purchase ended in error again:",
    zh: "该采购再次处理失败：",
  },
  "reproceso.falloSinDetalle": {
    es: "La compra volvió a quedar en error.",
    en: "The purchase ended in error again.",
    zh: "该采购再次处理失败。",
  },
  "respuesta.yaRespondida": {
    es: "Esta factura ya tenía una respuesta enviada.",
    en: "This invoice already had a response sent.",
    zh: "该发票此前已发送过答复。",
  },


  // --- plazo del SII (8 días desde la recepción) ----------------------------
  "plazo.titulo": { es: "Plazo del SII", en: "SII deadline", zh: "SII 期限" },
  "plazo.todas": { es: "Todas", en: "All", zh: "全部" },
  "plazo.criticas": { es: "Críticas · 1 o 2 días", en: "Critical · 1–2 days left", zh: "紧急 · 剩 1–2 天" },
  "plazo.porVencer": { es: "Por vencer · esta semana", en: "Due soon · this week", zh: "即将到期 · 本周" },
  "plazo.recientes": { es: "Recién llegadas", en: "Just arrived", zh: "新到单据" },
  // "Vencida" describe lo que NO pasó. "Aceptada por el SII" describe lo que
  // sí pasó, que es además lo correcto en términos legales: la factura fue
  // aceptada tácitamente, no caducó.
  "plazo.vencidas": {
    es: "Aceptadas por el SII · ya no se pueden reclamar",
    en: "Accepted by the SII · can no longer be claimed",
    zh: "SII 已接受 · 无法再提出异议",
  },
  "plazo.explicacion": {
    es: "Hay 8 días desde la recepción para aceptar o reclamar una factura. Pasado ese plazo el SII la da por aceptada y ya no se puede reclamar. Solo se cuentan facturas sin responder.",
    en: "There are 8 days from receipt to accept or claim an invoice. After that the SII treats it as accepted and it can no longer be claimed. Only unanswered invoices are counted.",
    zh: "自收到发票起有 8 天时间接受或提出异议。超过该期限，SII 将视为已接受，届时无法再提出异议。仅统计尚未答复的发票。",
  },
  "plazo.quedan": { es: "Quedan", en: "Days left:", zh: "剩余" },
  "plazo.ultimoDia": { es: "Último día", en: "Last day", zh: "最后一天" },
  "plazo.restanteAyuda": {
    es: "Días que quedan para responderle al proveedor antes de que el SII acepte la factura automáticamente.",
    en: "Days left to respond to the supplier before the SII accepts the invoice automatically.",
    zh: "在 SII 自动视为接受之前，可答复供应商的剩余天数。",
  },
  "plazo.vencida": { es: "Aceptada por el SII", en: "Accepted by the SII", zh: "SII 已接受" },
  "plazo.vencidaAyuda": {
    es: "Pasaron los 8 días sin respuesta, así que el SII la dio por aceptada. La factura se debe y ya no se puede reclamar.",
    en: "The 8 days passed with no response, so the SII treated it as accepted. The invoice is owed and can no longer be claimed.",
    zh: "8 天内未作答复，SII 已视为接受。该发票需支付，且无法再提出异议。",
  },


  "plazo.dia": { es: "día", en: "day", zh: "天" },
  "plazo.dias": { es: "días", en: "days", zh: "天" },

  // --- idioma ---------------------------------------------------------------
  "idioma.titulo": { es: "Idioma", en: "Language", zh: "语言" },
};

/**
 * Devuelve el texto en el idioma pedido.
 * Si falta una traducción, cae al español en vez de mostrar la clave cruda:
 * es preferible una palabra en español a ver "col.folio" en pantalla.
 */
export function traducir(clave, idioma = IDIOMA_POR_DEFECTO, respaldo) {
  const entrada = textos[clave];
  // Si la clave no existe, se muestra el `respaldo` que pasó quien llama —
  // normalmente el texto en español que ya estaba escrito a mano. Ver
  // "col.folio" en pantalla es peor que verlo sin traducir.
  if (!entrada) return respaldo ?? clave;
  return entrada[idioma] || entrada[IDIOMA_POR_DEFECTO] || respaldo || clave;
}

/**
 * Tiempo relativo traducido: "hace 2 h" / "2 h ago" / "2 小时前".
 * El orden de las palabras cambia por idioma, así que cada uno arma su frase.
 */
export function tiempoRelativo(cantidad, claveUnidad, idioma = IDIOMA_POR_DEFECTO) {
  const unidad = traducir(claveUnidad, idioma);
  if (idioma === "zh") return `${cantidad} ${unidad}前`;
  if (idioma === "en") return `${cantidad} ${unidad} ago`;
  return `hace ${cantidad} ${unidad}`;
}

export default textos;
